import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Camera, Upload, Loader2, SearchX, Copy, ChevronDown, ChevronUp, RefreshCcw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import { getApiUrl, getServerName, getSystemID, getLocalApiUrl, authFetch } from '../../config/api';
import { getProfileConfidenceBadgeClasses, getConfidenceLevelFromScore } from '../../lib/profileConfidence';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';


import { formatSydneyDateTimeSec } from '../../utils/sydneyDateTime'
const SHOW_ACCOUNT_ID_SEARCH = false
const SHOW_TOUCH_POINTS_SEARCH = false

function BulkPatronRiskRecalcButton({ toast }) {
  const [starting, setStarting] = useState(false)
  const [job, setJob] = useState(null)
  const pollRef = useRef(null)

  const applyJob = (data) => {
    if (!data) return
    setJob(data)
    if (data.status === 'completed' || data.status === 'failed') {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      if (data.status === 'completed') {
        toast({
          title: 'Bulk risk recalculation finished',
          description: `${data.processed ?? 0} of ${data.total ?? 0} patrons updated${data.failed ? ` (${data.failed} failed)` : ''}.`,
        })
      } else {
        toast({
          variant: 'destructive',
          title: 'Bulk risk recalculation failed',
          description: data.error || data.last_error || 'See server logs for details.',
        })
      }
    }
  }

  const pollStatus = async (jobId) => {
    try {
      const path = jobId
        ? `/api/profiles/risk-scores/bulk-recalculate/${encodeURIComponent(jobId)}`
        : '/api/profiles/risk-scores/bulk-recalculate/status'
      const res = await authFetch(getLocalApiUrl(path))
      const json = await res.json()
      if (res.ok && json?.success && json.data) applyJob(json.data)
    } catch (_) {
      /* keep polling */
    }
  }

  useEffect(() => {
    pollStatus(null)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const running = job && (job.status === 'queued' || job.status === 'running')

  const handleStart = async () => {
    const ok = window.confirm(
      'Recalculate risk for active patrons only?\n\nArchived / inactive members are skipped. This runs in the background and may take several minutes for large member lists.'
    )
    if (!ok) return
    setStarting(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/profiles/risk-scores/bulk-recalculate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: 'all' }),
      })
      const json = await res.json()
      if (!res.ok || !json?.success) {
        if (res.status === 409 && json?.data) {
          applyJob(json.data)
          if (!pollRef.current) {
            pollRef.current = setInterval(() => pollStatus(json.data.job_id), 3000)
          }
          toast({
            title: 'Recalculation already running',
            description: `${json.data.processed ?? 0}/${json.data.total ?? '…'} active patrons processed so far.`,
          })
          return
        }
        throw new Error(json?.error || 'Failed to start bulk recalculation')
      }
      applyJob(json.data)
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(() => pollStatus(json.data.job_id), 3000)
      toast({
        title: 'Risk recalculation started',
        description: 'Active patrons only — running in the background.',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not start bulk recalculation',
        description: err?.message || 'Unknown error',
      })
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-xs h-8"
        disabled={starting || running}
        onClick={handleStart}
        title="Recalculates risk for active members only (excludes archived / inactive)"
      >
        <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${running ? 'animate-spin' : ''}`} />
        {starting ? 'Starting…' : running ? 'Recalculating active patrons…' : 'Recalculate active patron risk'}
      </Button>
      {job && (
        <p className="text-[11px] text-gray-500 text-right max-w-xs">
          {job.status}: {job.processed ?? 0}/{job.total ?? '…'} processed
          {job.failed ? ` · ${job.failed} failed` : ''}
        </p>
      )}
    </div>
  )
}

function mapFaceCandidateToResult(candidate) {
  const ud = candidate?.profilesafe_user_data || {}
  const patron = candidate?.patron
  const fn = patron?.first_name || ud.FirstName || ud.FN || ''
  const ln = patron?.last_name || ud.LastName || ud.LN || ''
  const mid = patron?.member_id || ud.MemberID || ud.MID || ''
  const mp = patron?.master_profile_id || ud.PatronID || ud.PID || ''
  const confPct = Math.round((candidate?.identification_confidence || 0) * 100)
  return {
    MasterProfileID: mp,
    FN: fn,
    LN: ln,
    MDN: '',
    MID: mid,
    MembershipNumber: patron?.membership_number || ud.MembershipNumber || '',
    DOB: patron?.date_of_birth || ud.DateOfBirth || ud.DOB || '',
    DVSStatus: patron?.dvs_status || 'N/A',
    RiskLevel: patron?.risk_level || 'N/A',
    ProfilePoint: confPct,
    _faceMatch: true,
    _matchBand: candidate?.match_band || 'low',
    _profilesafeProfileId: candidate?.profilesafe_profile_id || '',
    _noLocalPatron: !patron,
    _profileType: String(patron?.profile_type || ud.ProfileType || '').toUpperCase(),
  }
}

function filterFaceCandidatesByText(candidates, searchParams) {
  const first = String(searchParams.firstName || '').trim().toLowerCase()
  const last = String(searchParams.lastName || '').trim().toLowerCase()
  const membershipNumber = String(searchParams.membershipNumber || '').trim().toLowerCase()
  const masterId = String(searchParams.masterProfileId || '').trim().toLowerCase()
  if (!first && !last && !membershipNumber && !masterId) return candidates
  return (candidates || []).filter((c) => {
    const row = mapFaceCandidateToResult(c)
    if (membershipNumber) {
      if (String(row.MembershipNumber || '').toLowerCase() !== membershipNumber) return false
      if (row._profileType === 'VISITOR') return false
    }
    if (masterId && String(row.MasterProfileID || '').toLowerCase() !== masterId) return false
    if (first && !String(row.FN || '').toLowerCase().includes(first)) return false
    if (last && !String(row.LN || '').toLowerCase().includes(last)) return false
    return true
  })
}

const formatGenderLabel = (g) => {
  if (!g) return 'N/A'
  const u = String(g).toUpperCase()
  if (u === 'M' || u === 'MALE') return 'Male'
  if (u === 'F' || u === 'FEMALE') return 'Female'
  if (u === 'X' || u === 'OTHER') return 'Other'
  if (u === 'UNKNOWN') return 'Unknown'
  return g
}

const ProfileSearch = ({ onSelectProfile, searchState, onSearchStateChange }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isPageEnabled } = useFeatureFlags();
  const batchKycEnabled = isPageEnabled('/profile-safe/batch-kyc');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSearchMode, setLastSearchMode] = useState(null);
  // Accordion state for form fields
  const [expandedFields, setExpandedFields] = useState(new Set());
  /** Member / master IDs selected for Batch KYC */
  const [batchSelectedIds, setBatchSelectedIds] = useState(() => new Set());
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50, // Default to 50 (minimum)
    totalCount: 0,
    hasMorePages: false
  });
  
  // Use search state from props instead of local state
  const { searchParams, searchResults, uploadedImageBase64, uploadedImagePreview, hasSearched } = searchState;

  const batchSelectKey = (result) => {
    const mid = result?.MID != null ? String(result.MID).trim() : ''
    const mp = result?.MasterProfileID != null ? String(result.MasterProfileID).trim() : ''
    return mid || mp || ''
  }

  const toggleBatchSelect = (result, e) => {
    e?.stopPropagation?.()
    const key = batchSelectKey(result)
    if (!key || result?._noLocalPatron) return
    setBatchSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const sendSelectedToBatchKyc = () => {
    const ids = [...batchSelectedIds]
    if (!ids.length) {
      toast({ title: 'Select patrons first', description: 'Tick one or more search results for Batch KYC.' })
      return
    }
    navigate(`/profile-safe/batch-kyc?memberIds=${encodeURIComponent(ids.join(','))}`)
  }

  const hasAgeFilter = () => {
    const mode = searchParams.ageFilter
    if (!mode || mode === 'ALL') return false
    if (mode === 'between') {
      return searchParams.ageMin !== '' && searchParams.ageMax !== ''
        && Number(searchParams.ageMin) <= Number(searchParams.ageMax)
    }
    return searchParams.ageValue !== '' && Number(searchParams.ageValue) >= 0
  }

  /** Count how many results share the same first+last display name (different Member IDs are still separate people). */
  const duplicateDisplayNameCounts = useMemo(() => {
    const c = {}
    for (const r of searchResults || []) {
      const fn = String(r.FN || '').trim().toLowerCase()
      const ln = String(r.LN || '').trim().toLowerCase()
      if (!fn && !ln) continue
      const k = `${fn}|${ln}`
      c[k] = (c[k] || 0) + 1
    }
    return c
  }, [searchResults])

  // Helper function to check if field has value
  const hasFieldValue = (fieldName) => {
    switch (fieldName) {
      case 'masterProfileId': return !!searchParams.masterProfileId;
      case 'accountId': return !!searchParams.accountId;
      case 'membershipNumber': return !!searchParams.membershipNumber;
      case 'firstName': return !!searchParams.firstName;
      case 'lastName': return !!searchParams.lastName;
      case 'dateOfBirth': return !!searchParams.dateOfBirth;
      case 'gender': return !!searchParams.gender && searchParams.gender !== 'ALL';
      case 'age': return hasAgeFilter();
      case 'touchPoint': return !!searchParams.touchPoint;
      case 'riskLevel': return !!searchParams.riskLevel && searchParams.riskLevel !== 'ALL';
      case 'patronType': return !!searchParams.patronType && searchParams.patronType !== 'ALL';
      case 'facePhoto': return !!uploadedImagePreview;
      default: return false;
    }
  };
  const toggleField = (fieldName) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldName)) {
      newExpanded.delete(fieldName);
    } else {
      newExpanded.add(fieldName);
    }
    setExpandedFields(newExpanded);
  };
  
  // Helper function to toggle accordion field expansion
  const updateSearchParams = (newParams) => {
    onSearchStateChange({
      ...searchState,
      searchParams: { ...searchParams, ...newParams }
    });
    // Reset pagination when search criteria changes
    setPagination(prev => ({ ...prev, currentPage: 1, totalCount: 0, hasMorePages: false }));
  };
  
  const updateSearchResults = (results) => {
    onSearchStateChange({
      ...searchState,
      searchResults: results,
      hasSearched: true
    });
  };
  
  const updateUploadedImage = (base64, preview) => {
    onSearchStateChange({
      ...searchState,
      uploadedImageBase64: base64,
      uploadedImagePreview: preview
    });
  };

  const hasTextSearchCriteria = () =>
    Boolean(
      searchParams.masterProfileId?.trim() ||
      (SHOW_ACCOUNT_ID_SEARCH && searchParams.accountId?.trim()) ||
      searchParams.membershipNumber?.trim() ||
      searchParams.firstName?.trim() ||
      searchParams.lastName?.trim() ||
      searchParams.dateOfBirth?.trim() ||
      (searchParams.gender && searchParams.gender !== 'ALL') ||
      hasAgeFilter() ||
      (SHOW_TOUCH_POINTS_SEARCH && searchParams.touchPoint?.trim()) ||
      (searchParams.riskLevel && searchParams.riskLevel !== 'ALL') ||
      (searchParams.patronType && searchParams.patronType !== 'ALL')
    )

  const callFaceIdentifyAPI = async () => {
    const res = await authFetch(getLocalApiUrl('/api/profiles/face-identify'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image_base64: uploadedImageBase64,
        context: 'profile_search',
        smart_crop: true,
      }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.success) {
      throw new Error(json.error || `Face identify failed (HTTP ${res.status})`)
    }
    return json.data || { candidates: [] }
  }

  // Make dynamic API call to GetProfile endpoint
  const callGetProfileAPI = async () => {
    try {
      const apiUrl = getApiUrl('GET_PROFILE');

      const requestPayload = {
        ServerName: getServerName(),
        SystemID: getSystemID(),
        FaceImgB64: '',
        MasterProfileID: searchParams.masterProfileId || "",
        MembershipNumber: searchParams.membershipNumber || "",
        AccountID: searchParams.accountId || "",
        Firstname: searchParams.firstName || "",
        Lastname: searchParams.lastName || "",
        DOB: searchParams.dateOfBirth || "",
        Gender: searchParams.gender || "",
        AgeFilter: searchParams.ageFilter && searchParams.ageFilter !== 'ALL' ? searchParams.ageFilter : "",
        AgeValue: searchParams.ageValue || "",
        AgeMin: searchParams.ageMin || "",
        AgeMax: searchParams.ageMax || "",
        TouchPoint: SHOW_TOUCH_POINTS_SEARCH ? (searchParams.touchPoint || "") : "",
        RiskLevel: searchParams.riskLevel || "",
        PatronType: searchParams.patronType && searchParams.patronType !== 'ALL' ? searchParams.patronType : "",
        MembershipStatus: searchParams.membershipStatus && searchParams.membershipStatus !== 'ALL' ? searchParams.membershipStatus : "",
        IncludeInactive: searchParams.includeInactive === true,
        Purpose: "GETPROFILE",
        Source: "RXPROFILEWEB",
        DeviceType: "RXPROFILEWEB",
        PageSize: pagination.pageSize.toString(),
        PageNumber: pagination.currentPage.toString()
      };

      console.log('Making API call to:', apiUrl);
      console.log('Request payload:', requestPayload);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API response:', data);
      console.log('ResponseCode:', data.ResponseCode);
      console.log('OutputData:', data.OutputData);
      console.log('OutputData length:', data.OutputData?.length);
      
      return data;
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  };

  const handleSearch = async () => {
    console.log('🔍 handleSearch called with params:', searchParams);
    setIsLoading(true);

    try {
      // Check if we have any search criteria (use the same logic as hasAnySearchCriteria)
      const hasSearchCriteria = searchParams.masterProfileId ||
                               (SHOW_ACCOUNT_ID_SEARCH && searchParams.accountId) ||
                               searchParams.membershipNumber ||
                               searchParams.firstName ||
                               searchParams.lastName ||
                               searchParams.dateOfBirth ||
                               (searchParams.gender && searchParams.gender !== 'ALL') ||
                               hasAgeFilter() ||
                               (SHOW_TOUCH_POINTS_SEARCH && searchParams.touchPoint) ||
                               (searchParams.riskLevel && searchParams.riskLevel !== 'ALL') ||
                               (searchParams.patronType && searchParams.patronType !== 'ALL') ||
                               uploadedImageBase64;

      console.log('hasSearchCriteria:', hasSearchCriteria);

      if (!hasSearchCriteria) {
        toast({
          title: "Search Criteria Required",
          description: "Please enter at least one search parameter, select a risk level or patron type, or upload a face photo.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Reset to page 1 if this is a new search (not pagination)
      if (pagination.currentPage === 1) {
        setPagination(prev => ({ ...prev, totalCount: 0, hasMorePages: false }));
      }

      const useFaceIdentify = Boolean(uploadedImageBase64?.trim())

      if (useFaceIdentify) {
        const faceData = await callFaceIdentifyAPI()
        let candidates = faceData.candidates || []
        if (hasTextSearchCriteria()) {
          candidates = filterFaceCandidatesByText(candidates, searchParams)
        }
        const rows = candidates.map(mapFaceCandidateToResult)
        setLastSearchMode('face_identify')
        updateSearchResults(rows)
        setPagination(prev => ({
          ...prev,
          totalCount: rows.length,
          hasMorePages: false,
        }))
        if (rows.length > 0) {
          toast({
            title: 'Face search completed',
            description: `Found ${rows.length} enrolled profile match(es) via ProfileSafe.`,
          })
        } else {
          toast({
            title: 'No face matches',
            description: 'No enrolled ProfileSafe profiles matched this photo. Face search only finds patrons with face enrollment.',
          })
        }
        setIsLoading(false)
        return
      }

      setLastSearchMode('sql')
      const data = await callGetProfileAPI();

      console.log('=== Search Handler Results ===');
      console.log('data:', data);
      console.log('ResponseCode:', data.ResponseCode);
      console.log('OutputData:', data.OutputData);

      if (data.ResponseCode === "0") {
        try {
          console.log('✅ Success! Setting search results:', data.OutputData);
          updateSearchResults(data.OutputData || []);
          
          // Update pagination info
          setPagination(prev => ({
            ...prev,
            totalCount: data.TotalCount || data.OutputData?.length || 0,
            hasMorePages: data.HasMorePages || false
          }));
          
          if (data.OutputData && data.OutputData.length > 0) {
            const totalText = data.TotalCount ? ` (${data.TotalCount} total)` : '';
            toast({
              title: "Search Completed",
              description: `Found ${data.OutputData.length} profile(s) matching your criteria${totalText}.`,
            });
          } else {
            // Check if filters were ignored due to missing columns
            const ignoredFilters = data.Meta?.ignoredFilters || [];
            if (ignoredFilters.length > 0) {
              toast({
                title: "Search Filter Not Available",
                description: `The ${ignoredFilters.join(', ')} filter could not be applied. Try adding name or date of birth criteria instead.`,
                variant: "destructive",
              });
            } else {
              toast({
                title: "No Results",
                description: "No profiles found matching your search criteria.",
              });
            }
          }
        } catch (innerError) {
          console.error('❌ Error processing search results:', innerError);
          throw innerError;
        }
      } else {
        toast({
          title: "Search Failed",
          description: data.ResponseMessage || "An error occurred during search.",
          variant: "destructive",
        });
        updateSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search Failed",
        description: "Could not connect to the profile service. Please try again later.",
        variant: "destructive",
      });
      updateSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (file) => {
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      const base64 = result.split(',')[1]; // Remove data:image/... prefix
      updateUploadedImage(base64, result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    }
  };

  const handleFacePaste = (e) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) handleImageUpload(file)
        break
      }
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearUploadedImage = () => {
    updateUploadedImage('', '');
  };

  const getProfilePointBadgeColor = (profilePoint) => {
    return getProfileConfidenceBadgeClasses(getConfidenceLevelFromScore(profilePoint ?? 0));
  };

  const getDVSStatusBadgeColor = (dvsStatus) => {
    switch (dvsStatus?.toUpperCase()) {
      case 'VERIFIED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'FAILED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getMembershipStatusBadgeColor = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'financial':
      case 'active':
      case 'current':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'lapsed':
      case 'inactive':
      case 'expired':
        return 'bg-amber-100 text-amber-900 border-amber-300'
      case 'archived':
      case 'cancelled':
      case 'canceled':
      case 'closed':
      case 'terminated':
      case 'resigned':
        return 'bg-slate-200 text-slate-800 border-slate-400'
      case 'suspended':
      case 'deleted':
        return 'bg-red-100 text-red-800 border-red-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  };

  const isNonActivePatron = (result) => {
    const profileStatus = String(result?.ProfileStatus || '').trim().toUpperCase()
    if (profileStatus && profileStatus !== 'ACTIVE') return true
    const membership = String(result?.MembershipStatus || '').trim().toLowerCase()
    if (!membership) return false
    return [
      'archived', 'lapsed', 'inactive', 'cancelled', 'canceled', 'expired',
      'suspended', 'deleted', 'closed', 'terminated', 'non-financial',
      'nonfinancial', 'unfinancial', 'resigned', 'rejected',
    ].includes(membership)
  };

  const nonActivePatronLabel = (result) => {
    const profileStatus = String(result?.ProfileStatus || '').trim().toUpperCase()
    if (profileStatus && profileStatus !== 'ACTIVE') {
      if (profileStatus === 'SUSPENDED') return 'Suspended — non-active member'
      if (profileStatus === 'DELETED') return 'Deleted — non-active member'
      return 'Inactive — non-active member'
    }
    const membership = String(result?.MembershipStatus || '').trim()
    return membership ? `${membership} — non-active member` : 'Non-active member'
  };

  // Check if any search criteria exists
  const hasAnySearchCriteria = () => {
    const hasCriteria = searchParams.masterProfileId?.trim() || 
           (SHOW_ACCOUNT_ID_SEARCH && searchParams.accountId?.trim()) || 
           searchParams.membershipNumber?.trim() || 
           searchParams.firstName?.trim() ||
           searchParams.lastName?.trim() ||
           searchParams.dateOfBirth?.trim() ||
           (searchParams.gender && searchParams.gender !== 'ALL') ||
           hasAgeFilter() ||
           (SHOW_TOUCH_POINTS_SEARCH && searchParams.touchPoint?.trim()) ||
           (searchParams.riskLevel && searchParams.riskLevel !== 'ALL') ||
           (searchParams.patronType && searchParams.patronType !== 'ALL') ||
           uploadedImageBase64?.trim();
    
    console.log('hasAnySearchCriteria check:', {
      hasCriteria,
      riskLevel: searchParams.riskLevel,
      riskLevelValid: searchParams.riskLevel && searchParams.riskLevel !== 'ALL',
      patronType: searchParams.patronType,
      patronTypeValid: searchParams.patronType && searchParams.patronType !== 'ALL',
      allParams: searchParams
    });
    
    return hasCriteria;
  };

  // Pagination functions
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    // Automatically trigger search with new page
    setTimeout(() => handleSearch(), 100);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      pageSize: newPageSize, 
      currentPage: 1 // Reset to first page when changing page size
    }));
    // Automatically trigger search with new page size
    setTimeout(() => handleSearch(), 100);
  };

  const renderResultsPagination = (placement = 'bottom') => {
    if (!hasSearched || searchResults.length === 0 || pagination.totalCount <= 0) {
      return null;
    }

    const placementClass = placement === 'top'
      ? 'mb-4 border-b border-gray-200'
      : 'mt-6 border-t border-gray-200';

    return (
      <div className={`px-4 py-4 bg-white rounded-lg ${placementClass}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of{' '}
              {pagination.totalCount} results
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Page Size:</label>
              <select
                value={pagination.pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
            >
              Previous
            </Button>

            <span className="text-sm text-gray-700">
              Page {pagination.currentPage} of {Math.ceil(pagination.totalCount / pagination.pageSize)}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasMorePages && pagination.currentPage >= Math.ceil(pagination.totalCount / pagination.pageSize)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Copy to clipboard function
  const copyToClipboard = (text, e) => {
    e.stopPropagation(); // Prevent card click event
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: 'Copied to clipboard',
        description: `Profile ID: ${text}`,
        duration: 2000,
      });
    }).catch(() => {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy to clipboard',
        variant: 'destructive',
        duration: 2000,
      });
    });
  };

  return (
    <div className="flex h-full bg-white">
      {/* Left Panel - Search Form */}
      <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
        {/* Scrollable Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="space-y-2">
            {/* Master Profile ID Search */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('masterProfileId')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('masterProfileId') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('masterProfileId') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Master Profile ID {hasFieldValue('masterProfileId') && `(${searchParams.masterProfileId})`}
                </span>
                {expandedFields.has('masterProfileId') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('masterProfileId') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="text"
                    value={searchParams.masterProfileId}
                    onChange={(e) => updateSearchParams({ masterProfileId: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Master Profile ID"
                    className="w-full placeholder:text-gray-500"
                  />
                </div>
              )}
            </div>

            {/* Account ID Search — hidden by default */}
            {SHOW_ACCOUNT_ID_SEARCH && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('accountId')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('accountId') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('accountId') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Account ID {hasFieldValue('accountId') && `(${searchParams.accountId})`}
                </span>
                {expandedFields.has('accountId') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('accountId') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="text"
                    value={searchParams.accountId}
                    onChange={(e) => updateSearchParams({ accountId: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Account ID"
                    className="w-full placeholder:text-gray-500"
                  />
                </div>
              )}
            </div>
            )}

            {/* Membership number search (club membership # only — not IGT player id) */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('membershipNumber')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('membershipNumber') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('membershipNumber') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Membership number {hasFieldValue('membershipNumber') && `(${searchParams.membershipNumber})`}
                </span>
                {expandedFields.has('membershipNumber') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('membershipNumber') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="text"
                    value={searchParams.membershipNumber}
                    onChange={(e) => updateSearchParams({ membershipNumber: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter membership number"
                    className="w-full placeholder:text-gray-500"
                  />
                </div>
              )}
            </div>

            {/* First Name Search */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('firstName')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('firstName') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('firstName') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  First Name {hasFieldValue('firstName') && `(${searchParams.firstName})`}
                </span>
                {expandedFields.has('firstName') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('firstName') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="text"
                    value={searchParams.firstName}
                    onChange={(e) => updateSearchParams({ firstName: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter First Name"
                    className="w-full placeholder:text-gray-500"
                  />
                </div>
              )}
            </div>

            {/* Last Name Search */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('lastName')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('lastName') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('lastName') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Last Name {hasFieldValue('lastName') && `(${searchParams.lastName})`}
                </span>
                {expandedFields.has('lastName') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('lastName') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="text"
                    value={searchParams.lastName}
                    onChange={(e) => updateSearchParams({ lastName: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Last Name"
                    className="w-full placeholder:text-gray-500"
                  />
                </div>
              )}
            </div>

            {/* Date of Birth Search */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('dateOfBirth')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('dateOfBirth') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('dateOfBirth') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Date of Birth {hasFieldValue('dateOfBirth') && `(${searchParams.dateOfBirth})`}
                </span>
                {expandedFields.has('dateOfBirth') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('dateOfBirth') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="date"
                    value={searchParams.dateOfBirth}
                    onChange={(e) => updateSearchParams({ dateOfBirth: e.target.value })}
                    onKeyPress={handleKeyPress}
                    className="w-full text-gray-900 placeholder:text-gray-500"
                  />
                </div>
              )}
            </div>

            {/* Gender */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('gender')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('gender') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('gender') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Gender {hasFieldValue('gender') && `(${formatGenderLabel(searchParams.gender)})`}
                </span>
                {expandedFields.has('gender') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('gender') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <select
                    value={searchParams.gender || 'ALL'}
                    onChange={(e) => updateSearchParams({ gender: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                  >
                    <option value="ALL">Any gender</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="X">Other</option>
                    <option value="UNKNOWN">Unknown / not set</option>
                  </select>
                </div>
              )}
            </div>

            {/* Age */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('age')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('age') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('age') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Age{' '}
                  {hasFieldValue('age') && searchParams.ageFilter === 'below' && `(below ${searchParams.ageValue})`}
                  {hasFieldValue('age') && searchParams.ageFilter === 'above' && `(above ${searchParams.ageValue})`}
                  {hasFieldValue('age') && searchParams.ageFilter === 'between' && `(${searchParams.ageMin}–${searchParams.ageMax})`}
                </span>
                {expandedFields.has('age') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('age') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50 space-y-3">
                  <select
                    value={searchParams.ageFilter || 'ALL'}
                    onChange={(e) => updateSearchParams({ ageFilter: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  >
                    <option value="ALL">Any age</option>
                    <option value="below">Below age</option>
                    <option value="above">Above age</option>
                    <option value="between">Between ages</option>
                  </select>
                  {searchParams.ageFilter === 'between' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">From (years)</label>
                        <Input
                          type="number"
                          min="0"
                          max="120"
                          value={searchParams.ageMin}
                          onChange={(e) => updateSearchParams({ ageMin: e.target.value })}
                          placeholder="Min"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">To (years)</label>
                        <Input
                          type="number"
                          min="0"
                          max="120"
                          value={searchParams.ageMax}
                          onChange={(e) => updateSearchParams({ ageMax: e.target.value })}
                          placeholder="Max"
                          className="w-full"
                        />
                      </div>
                    </div>
                  ) : searchParams.ageFilter && searchParams.ageFilter !== 'ALL' ? (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Age (years)</label>
                      <Input
                        type="number"
                        min="0"
                        max="120"
                        value={searchParams.ageValue}
                        onChange={(e) => updateSearchParams({ ageValue: e.target.value })}
                        placeholder="e.g. 25"
                        className="w-full"
                      />
                    </div>
                  ) : null}
                  <p className="text-xs text-gray-500">Uses date of birth from patron profile (DimPatron import).</p>
                </div>
              )}
            </div>

            {/* Face Photo Upload */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('facePhoto')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('facePhoto') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('facePhoto') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Face Photo Search {hasFieldValue('facePhoto') && '(Uploaded)'}
                </span>
                {expandedFields.has('facePhoto') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('facePhoto') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  {uploadedImagePreview ? (
                    <div className="border-2 border-green-300 rounded-lg p-4 text-center bg-green-50">
                      <div className="flex justify-center mb-3">
                        <img 
                          src={uploadedImagePreview} 
                          alt="Uploaded face" 
                          className="w-24 h-24 rounded-lg object-cover border-2 border-green-400"
                        />
                      </div>
                      <p className="text-sm text-green-700 font-medium mb-2">
                        Face photo uploaded successfully
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={clearUploadedImage}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Remove Image
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div 
                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer outline-none focus:border-blue-400"
                        tabIndex={0}
                        onPaste={handleFacePaste}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('face-photo-input')?.click()}
                      >
                        <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          Paste a screenshot (Ctrl+V / Cmd+V), drag and drop, or click to upload
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Searches ProfileSafe face-enrolled patrons only
                        </p>
                        <p className="text-xs text-gray-400">
                          Uploaded Size : Max 5MB
                        </p>
                      </div>
                      
                      <input
                        id="face-photo-input"
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                    </>
                  )}
                  
                  <div className="mt-3">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => document.getElementById('face-photo-input')?.click()}
                      disabled={!!uploadedImagePreview}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Face Photo
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Touch Points — hidden by default */}
            {SHOW_TOUCH_POINTS_SEARCH && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('touchPoint')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('touchPoint') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('touchPoint') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Touch Points {hasFieldValue('touchPoint') && `(${searchParams.touchPoint})`}
                </span>
                {expandedFields.has('touchPoint') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('touchPoint') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <Input
                    type="text"
                    value={searchParams.touchPoint}
                    onChange={(e) => updateSearchParams({ touchPoint: e.target.value })}
                    placeholder="Touch point"
                    className="w-full"
                  />
                </div>
              )}
            </div>
            )}

            {/* Risk Level Filter */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('riskLevel')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('riskLevel') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('riskLevel') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Risk Level {hasFieldValue('riskLevel') && searchParams.riskLevel !== 'ALL' && `(${searchParams.riskLevel})`}
                </span>
                {expandedFields.has('riskLevel') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('riskLevel') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={searchParams.riskLevel || 'ALL'}
                    onChange={(e) => {
                      console.log('Risk level selected:', e.target.value);
                      updateSearchParams({ riskLevel: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    <option value="ALL">All Risk Levels</option>
                    <option value="LOW">Low Risk</option>
                    <option value="MEDIUM">Medium Risk</option>
                    <option value="HIGH">High Risk</option>
                    <option value="CRITICAL">Critical Risk</option>
                  </select>
                </div>
              )}
            </div>

            {/* Patron Type Filter */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('patronType')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  hasFieldValue('patronType') ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  hasFieldValue('patronType') ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Patron Type {hasFieldValue('patronType') && searchParams.patronType !== 'ALL' && `(${searchParams.patronType})`}
                </span>
                {expandedFields.has('patronType') ? 
                  <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('patronType') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={searchParams.patronType || 'ALL'}
                    onChange={(e) => updateSearchParams({ patronType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    <option value="ALL">All Patrons</option>
                    <option value="MEMBER">Members Only</option>
                    <option value="VISITOR">Visitors Only</option>
                  </select>
                </div>
              )}
            </div>

            {/* Membership Status Filter */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleField('membershipStatus')}
                className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                  searchParams.membershipStatus && searchParams.membershipStatus !== 'ALL' ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <span className={`text-sm font-medium ${
                  searchParams.membershipStatus && searchParams.membershipStatus !== 'ALL' ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Membership Status
                  {searchParams.membershipStatus && searchParams.membershipStatus !== 'ALL' && ` (${searchParams.membershipStatus})`}
                  {!searchParams.includeInactive && (!searchParams.membershipStatus || searchParams.membershipStatus === 'ALL') && (
                    <span className="ml-1.5 text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded">Active only</span>
                  )}
                </span>
                {expandedFields.has('membershipStatus') ?
                  <ChevronUp className="h-4 w-4 text-gray-500" /> :
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                }
              </button>
              {expandedFields.has('membershipStatus') && (
                <div className="p-3 border-t border-gray-200 bg-gray-50 space-y-3" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={searchParams.membershipStatus || 'ALL'}
                    onChange={(e) => updateSearchParams({ membershipStatus: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white cursor-pointer"
                  >
                    <option value="ALL">All vendor statuses</option>
                    <option value="Financial">Financial (IGT active)</option>
                    <option value="Active">Active</option>
                    <option value="Lapsed">Lapsed</option>
                    <option value="Archived">Archived</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!searchParams.includeInactive}
                      onChange={(e) => updateSearchParams({ includeInactive: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    Include inactive / archived members
                  </label>
                  <p className="text-xs text-gray-500">By default only active members are shown (normalized profile status). Vendor labels like IGT &quot;Financial&quot; are mapped at import. Enable this to find former or lapsed members.</p>
                </div>
              )}
            </div>

            {/* Search Button as Last Accordion Item */}
            <div className="border border-gray-200 rounded-lg mt-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Button 
                  className="w-full" 
                  onClick={handleSearch} 
                  disabled={isLoading || !hasAnySearchCriteria()}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search Profiles
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Search Results */}
      <div className="flex-1 p-6 bg-gray-50">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Search results</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Bulk risk runs asynchronously on the server — safe for large member lists.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <BulkPatronRiskRecalcButton toast={toast} />
            {batchKycEnabled && (
              <div className="flex items-center gap-2">
                {batchSelectedIds.size > 0 && (
                  <span className="text-xs text-gray-500">{batchSelectedIds.size} selected</span>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs h-8"
                  disabled={!batchSelectedIds.size}
                  onClick={sendSelectedToBatchKyc}
                >
                  Add to Batch KYC
                </Button>
              </div>
            )}
          </div>
        </div>

        {renderResultsPagination('top')}

        {/* Results List */}
        <div className="space-y-4">
          {searchResults.map((result) => {
            const displayNameKey = `${String(result.FN || '').trim().toLowerCase()}|${String(result.LN || '').trim().toLowerCase()}`
            const sameNameMultipleMembers = (duplicateDisplayNameCounts[displayNameKey] || 0) > 1

            // Format date time as DD MMM YYYY HH:MM:SS
            const formatDateTime = (dateTimeStr) => (dateTimeStr ? formatSydneyDateTimeSec(dateTimeStr) : 'N/A')

            const selectKey = batchSelectKey(result)
            const isBatchSelected = selectKey && batchSelectedIds.has(selectKey)
            const nonActive = isNonActivePatron(result)
            return (
              <Card 
                key={result.MasterProfileID || result._profilesafeProfileId || `${result.FN}-${result.LN}-${i}`}
                className={`overflow-hidden transition-shadow duration-200 ${result._noLocalPatron ? 'cursor-default' : 'cursor-pointer hover:shadow-md'} ${batchKycEnabled && isBatchSelected ? 'ring-2 ring-teal-400' : ''} ${nonActive ? 'border-amber-300 bg-amber-50/40 ring-1 ring-amber-200' : ''}`}
                onClick={() => {
                  if (result._noLocalPatron || !result.MasterProfileID) {
                    toast({
                      title: 'Not in member database',
                      description: 'ProfileSafe matched this face but there is no local patron profile to open.',
                    })
                    return
                  }
                  onSelectProfile(result.MasterProfileID)
                }}
              >
                <CardContent className="p-0">
                  <div className="flex p-4">
                    {batchKycEnabled && !result._noLocalPatron && selectKey && (
                      <div className="pr-3 pt-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-600"
                          checked={!!isBatchSelected}
                          onChange={(e) => toggleBatchSelect(result, e)}
                          aria-label={`Select ${selectKey} for Batch KYC`}
                        />
                      </div>
                    )}
                    <div className="flex-1 space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mr-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-gray-100 text-gray-400 flex items-center justify-center">
                              <Camera className="h-6 w-6" />
                            </AvatarFallback>
                          </Avatar>
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {`${result.FN || 'N/A'} ${result.MDN || ''} ${result.LN || 'N/A'}`.trim()}
                              </h3>
                              {nonActive && (
                                <Badge className="bg-amber-200 text-amber-950 border border-amber-400 text-xs font-semibold">
                                  {nonActivePatronLabel(result)}
                                </Badge>
                              )}
                            </div>
                            {result._faceMatch && (
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge className={getProfilePointBadgeColor(result.ProfilePoint)}>
                                  Face match {result.ProfilePoint}%
                                </Badge>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {result._matchBand} confidence
                                </Badge>
                                {result._noLocalPatron && (
                                  <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-xs">
                                    ProfileSafe only — not in member DB
                                  </Badge>
                                )}
                              </div>
                            )}
                            {sameNameMultipleMembers && (
                              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2 max-w-lg">
                                Another profile in this list shares this name — these are <span className="font-semibold">different member accounts</span>. Use membership number and ID to tell them apart.
                              </p>
                            )}
                            <div className="text-sm text-gray-500 mt-1">
                              DOB: {result.DOB || 'N/A'}
                              {result.Age != null && result.Age !== '' && (
                                <> · Age: {result.Age}</>
                              )}
                              {result.Venue && (
                                <> · {result.Venue}</>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                              ID <span className="font-mono ml-1 bg-slate-100 px-1.5 py-0.5 rounded text-xs">{result.MasterProfileID}</span>
                              <button
                                onClick={(e) => copyToClipboard(result.MasterProfileID, e)}
                                className="ml-1 p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Copy ID"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 border-t pt-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Membership No</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {result.MembershipNumber ? (
                              <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                                {result.MembershipNumber}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Player ID</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {result.MID ? (
                              <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{result.MID}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Membership Status</p>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {result.MembershipStatus ? (
                              <Badge className={`text-xs border ${getMembershipStatusBadgeColor(result.MembershipStatus)}`}>
                                {result.MembershipStatus}
                              </Badge>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                            {nonActive && (
                              <Badge className="bg-amber-200 text-amber-950 border border-amber-400 text-xs font-semibold">
                                Non-active
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Membership Type</p>
                          <p className="text-sm font-semibold text-gray-900">{result.MembershipType || <span className="text-gray-400">—</span>}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Expiry</p>
                          <p className="text-sm font-semibold text-gray-900">{result.MembershipExpiry || <span className="text-gray-400">—</span>}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Gender</p>
                          <p className="text-sm font-semibold text-gray-900">{formatGenderLabel(result.Gender)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Age</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {result.Age != null && result.Age !== '' ? `${result.Age} yrs` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">DVS Status</p>
                          <Badge className={getDVSStatusBadgeColor(result.DVSStatus)}>
                            {result.DVSStatus || 'N/A'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Risk Level</p>
                          <Badge className={`text-xs
                            ${result.RiskLevel === 'CRITICAL' ? 'bg-red-100 text-red-800 border-red-300' : ''}
                            ${result.RiskLevel === 'HIGH' ? 'bg-orange-100 text-orange-800 border-orange-300' : ''}
                            ${result.RiskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : ''}
                            ${result.RiskLevel === 'LOW' ? 'bg-green-100 text-green-800 border-green-300' : ''}
                            border
                          `}>
                            {result.RiskLevel || 'N/A'}
                          </Badge>
                        </div>
                        {SHOW_ACCOUNT_ID_SEARCH && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Account ID</p>
                          <p className="text-sm font-semibold text-gray-900">{result.AID || 'N/A'}</p>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {renderResultsPagination('bottom')}

        {/* Empty State */}
        {hasSearched && searchResults.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <SearchX className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No profiles found</h3>
            <p className="text-gray-500">
              {lastSearchMode === 'face_identify'
                ? 'No enrolled ProfileSafe profiles matched this photo. Try a clearer face crop or search by name/member number.'
                : 'Try adjusting your search criteria or upload a face photo for ProfileSafe face search.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSearch;